'use client';

import { apiFetch } from '@/lib/api-client';
import { useLocale } from 'next-intl';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { clearProspectsNavigationState } from '@/utils/stageNavCache';
import { captureError } from '@/utils/capture-error';
import { ImpersonationHandle } from '@/components/ImpersonationHandle';
import { adminImpersonationUrl } from '@/lib/api/client-routes';

/**
 * Browser authentication state after the session cutover (02-auth-cutover Task 5).
 *
 * The browser holds no credential and computes nothing about one. There is no token to
 * decode, no expiry to compare, no refresh timer and no shared refresh promise: the
 * session is an HttpOnly cookie the browser cannot read, and `GET /api/auth/session` is
 * the only source of the profile. Everything this file used to do — the ten-minute
 * interval, the `visibilitychange` re-check, the local expiry check — existed to manage
 * a token that no longer lives here, and each of them was also a way for the client to
 * decide it was logged out while the server disagreed.
 *
 * Expiry during a visit is handled where it actually happens: `apiFetch` turns a 401
 * into the localized login with a `returnUrl`, and retries the barrier's 503 instead of
 * reading it as a logout.
 *
 * `probeSession` decides whether this provider ASKS who the visitor is, and only the
 * `(crm)` layout turns it on. The provider itself wraps the whole locale tree because
 * the login page needs `login()` and the header needs `logout()` — but a public page has
 * anonymous visitors by design, and probing there would answer 401 on every marketing
 * page, every widget embed and the login page itself. Which pages are public is settled
 * by where their files live, not by a list of paths this file would have to keep in step.
 */

/** Keys the pre-cutover build left in web storage. Removed once, on first mount, so a
 *  returning browser does not keep a Cognito token nobody reads any more. */
const LEGACY_STORAGE_KEYS = [
  'accessToken', 'idToken', 'expiresIn', 'cognitoUsername', 'userEmail', 'user', 'auth.persist', 'admin_impersonate',
];

function purgeLegacyCredentials(): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    for (const key of LEGACY_STORAGE_KEYS) {
      try {
        store.removeItem(key);
      } catch {
        // Storage blocked (Firefox strict mode, embedded iframes): nothing to purge.
      }
    }
  }
}

export interface AuthProfile {
  userId: string;
  email: string;
  name: string;
  agencyId: string;
  agencyCountry?: 'Belgium' | 'France';
  userType: string;
  roles?: string[];
}

/** The Cognito challenges a staff login can stop on. */
export type ChallengeName = 'NEW_PASSWORD_REQUIRED' | 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';

export interface LoginResult {
  success: boolean;
  error?: string;
  /** Set when Cognito wants one more answer. The transaction lives server-side in
   *  `auth_pending`, keyed by an HttpOnly cookie; the browser holds nothing. */
  challenge?: ChallengeName;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** The profile `GET /api/auth/session` answered with. Typed loosely because the CRM
   *  screens read it as their own `User` shape; the server is the authority either way. */
  user: any | null;
  login: (email: string, password: string, keep?: boolean) => Promise<LoginResult>;
  /** Answers the challenge `login` reported, and mints the session on success. */
  completeChallenge: (answer: string) => Promise<{ success: boolean; error?: string }>;
  /** The challenge awaiting an answer, or null. */
  pendingChallenge: ChallengeName | null;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  clearError: () => void;
  /** True while the session acts as another user (`GET /api/auth/session` says so). */
  impersonating: boolean;
  /** Acts as `userId` server-side; true once the session answers as them. */
  startImpersonation: (userId: string) => Promise<boolean>;
  /** Returns to the administrator's own identity; true on success. */
  stopImpersonation: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** The profile shape `GET /api/auth/session` answers with. */
interface SessionResponse {
  authenticated?: boolean;
  user?: { userId: string; email: string; name: string };
  userType?: string;
  agencyId?: string;
  agencyCountry?: 'Belgium' | 'France';
  roles?: string[];
  impersonating?: { actorUserId: string; until: string };
}

function profileOf(body: SessionResponse): AuthProfile | null {
  if (!body.authenticated || !body.user?.userId || !body.agencyId) return null;
  return {
    userId: body.user.userId,
    email: body.user.email,
    name: body.user.name,
    agencyId: body.agencyId,
    agencyCountry: body.agencyCountry,
    userType: body.userType ?? '',
    roles: body.roles ?? [],
  };
}

export function AuthProvider({
  children,
  probeSession = false,
}: {
  children: ReactNode;
  /** Only the authenticated route group sets this. */
  probeSession?: boolean;
}) {
  const [user, setUser] = useState<AuthProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(probeSession);
  const [error, setError] = useState<string | null>(null);
  const locale = useLocale();

  /** Mirrors the session's impersonation; the server owns it and decides every request. */
  const [impersonation, setImpersonation] = useState<{ actorUserId: string; until: string } | null>(null);

  const loadProfile = useCallback(async (): Promise<boolean> => {
    try {
      // Never redirects: this call is the QUESTION "is anyone signed in?", and a 401 is
      // one of its two valid answers. Sending the browser to the login page for it turns
      // the login page's own probe into an infinite reload.
      const response = await apiFetch('/api/auth/session', undefined, { redirectOn401: false });
      if (!response.ok) {
        setUser(null);
        setImpersonation(null);
        return false;
      }
      const body = await response.json() as SessionResponse;
      const profile = profileOf(body);
      setUser(profile);
      setImpersonation(profile ? body.impersonating ?? null : null);
      return profile !== null;
    } catch (loadError) {
      captureError(loadError, { operation: 'auth.load-profile' });
      setUser(null);
      setImpersonation(null);
      return false;
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') purgeLegacyCredentials();
    if (!probeSession) return;
    let cancelled = false;
    void (async () => {
      await loadProfile();
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [probeSession, loadProfile]);

  useEffect(() => {
    if (!impersonation) return;
    // The server stops honouring the impersonation at `until`; reload then so the page
    // stops showing the agent's data alongside the administrator's identity.
    const remaining = new Date(impersonation.until).getTime() - Date.now();
    const timer = window.setTimeout(() => window.location.reload(), Math.max(0, remaining) + 1_000);
    return () => window.clearTimeout(timer);
  }, [impersonation]);

  const startImpersonation = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const response = await apiFetch(adminImpersonationUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      if (!response.ok) return false;
      return loadProfile();
    } catch (startError) {
      captureError(startError, { operation: 'auth.impersonation-start' });
      return false;
    }
  }, [loadProfile]);

  const stopImpersonation = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiFetch(adminImpersonationUrl(), { method: 'DELETE' });
      if (!response.ok) return false;
      return loadProfile();
    } catch (stopError) {
      captureError(stopError, { operation: 'auth.impersonation-stop' });
      return false;
    }
  }, [loadProfile]);

  const [pendingChallenge, setPendingChallenge] = useState<ChallengeName | null>(null);

  const login = async (email: string, password: string): Promise<LoginResult> => {
    setError(null);
    try {
      const response = await apiFetch('/api/auth/signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        // The server answers every refusal identically on purpose; the UI must not
        // invent a more specific message from a status.
        const message = 'Incorrect email or password';
        setError(message);
        return { success: false, error: message };
      }

      const body = await response.json() as SessionResponse & { challenge?: ChallengeName };
      if (body.challenge) {
        // Not a failure: the server holds the Cognito transaction and is waiting for one
        // more answer, which `completeChallenge` supplies.
        setPendingChallenge(body.challenge);
        return { success: false, challenge: body.challenge };
      }

      const profile = profileOf(body);
      if (!profile) {
        const message = 'Sign-in failed';
        setError(message);
        return { success: false, error: message };
      }
      setUser(profile);
      return { success: true };
    } catch (loginError: unknown) {
      const message = loginError instanceof Error ? loginError.message : 'An unexpected error occurred';
      captureError(loginError, { operation: 'auth.login' });
      setError(message);
      return { success: false, error: message };
    }
  };

  const completeChallenge = async (answer: string) => {
    setError(null);
    if (!pendingChallenge) return { success: false, error: 'Sign-in failed' };
    try {
      const response = await apiFetch('/api/auth/challenge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The route reads a new password or a one-time code; which one is decided by the
        // transaction it consumes, not by the browser.
        body: JSON.stringify(pendingChallenge === 'NEW_PASSWORD_REQUIRED' ? { newPassword: answer } : { code: answer }),
      }, { redirectOn401: false });

      if (!response.ok) {
        // A consumed transaction cannot be retried — it is one-time by design — so the
        // only way forward is to start the login again.
        const message = 'Sign-in failed. Please sign in again.';
        setPendingChallenge(null);
        setError(message);
        return { success: false, error: message };
      }

      const profile = profileOf(await response.json() as SessionResponse);
      if (!profile) {
        const message = 'Sign-in failed';
        setError(message);
        return { success: false, error: message };
      }
      setPendingChallenge(null);
      setUser(profile);
      return { success: true };
    } catch (challengeError: unknown) {
      const message = challengeError instanceof Error ? challengeError.message : 'An unexpected error occurred';
      captureError(challengeError, { operation: 'auth.complete-challenge' });
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    setError(null);
    try {
      // The local session dies first, and unconditionally: a hosted-provider redirect
      // that runs before it would leave this application's own cookie alive if the
      // provider round trip never came back.
      await apiFetch('/api/auth/signout', { method: 'POST' });
    } catch (logoutError) {
      captureError(logoutError, { operation: 'auth.logout' });
    }

    setUser(null);
    setImpersonation(null);
    setPendingChallenge(null);
    // sessionStorage survives the redirect below, so the next user in this tab would
    // otherwise inherit this user's lead navigation order.
    clearProspectsNavigationState();

    if (typeof window === 'undefined') return;
    // The same purge the provider runs on mount. A browser that signs out and stays on
    // the tab would otherwise keep whatever a pre-cutover build left behind until the
    // next mount, which is exactly the window a shared machine is used in.
    purgeLegacyCredentials();
    const cognitoDomain = process.env.NEXT_PUBLIC_COGNITO_DOMAIN;
    const clientId = process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID;
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    const logoutUri = `${baseUrl}/${locale}/auth/login`;

    if (cognitoDomain && clientId && baseUrl) {
      window.location.href = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
    } else {
      window.location.assign(`/${locale}/auth/login`);
    }
  };

  const refreshAuth = async () => loadProfile();

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: user !== null,
        isLoading,
        error,
        user,
        login,
        completeChallenge,
        pendingChallenge,
        logout,
        refreshAuth,
        clearError: () => setError(null),
        impersonating: impersonation !== null,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {impersonation && <ImpersonationHandle onExit={stopImpersonation} />}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
